import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useDebouncedValue } from 'src/hooks'

import {
  AVATAR_SETTLE_MS,
  HANDLE_MIN_LENGTH,
  STEP_ORDER,
  sanitizeHandleInput,
  slugifyHandle,
  stepIndexOf
} from './constants'
import { clearDraft, readDraft, writeDraft } from './draftRepo'
import type {
  DraftInvite,
  OnboardingDraft,
  SpotlightKey,
  StepKey
} from './types'

/** Field a step's validation error belongs to. */
export type StepErrorField =
  | 'name'
  | 'handle'
  | 'companySize'
  | 'industry'
  | 'priority'
  | 'invites'

/**
 * The current step's validation failure, or null when it passes.
 *
 * Carries the field as well as the message so each step can hand it to the
 * matching `Form.Item` — AntD then paints that control red and prints the text
 * underneath it, instead of the step ending in a banner that does not say which
 * control is at fault.
 */
export type StepError = { field: StepErrorField; key: string } | null

const newInvite = (): DraftInvite => ({
  id: crypto.randomUUID(),
  email: ''
})

const emptyDraft = (): OnboardingDraft => ({
  step: 'details',
  name: '',
  handle: '',
  handleEdited: false,
  companySize: null,
  industry: null,
  priority: null,
  attribution: null,
  joinDeclined: false,
  // One row only: "Add another" is right underneath, and a second empty row
  // reads as an expectation to fill it.
  invites: [newInvite(), newInvite(), newInvite()]
})

/**
 * Grace period before a hover clears. Long enough to cross the gap between two
 * neighbouring tiles, short enough that leaving the group feels immediate.
 */
const HOVER_CLEAR_DELAY_MS = 120

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i

/**
 * Exported so the invite rows can mark exactly the addresses that fail, rather
 * than reddening every row because one of them is wrong.
 */
export const isInviteEmail = (value: string): boolean =>
  EMAIL_PATTERN.test(value.trim())

/**
 * Rebuilds a draft on top of the current defaults.
 *
 * A stored draft is whatever an older build of this page wrote, so its shape can
 * lag behind the type. Layering the stored values over `emptyDraft()` makes any
 * past or future field addition a non-event; without it, a draft saved before a
 * field existed crashed every read.
 */
const normalizeDraft = (
  stored: Partial<OnboardingDraft> | null
): OnboardingDraft => {
  const base = emptyDraft()
  if (!stored) return base

  const draft: OnboardingDraft = {
    ...base,
    ...stored,
    invites: stored.invites?.length ? stored.invites : base.invites
  }

  return { ...draft, step: firstUnansweredStep(draft) }
}

/**
 * The furthest step the stored answers actually support.
 *
 * A draft written before the industry rework carries `preset` and no
 * `industry`, and one hand-edited in devtools can say anything at all. Resuming
 * on the step it claims would put the user on the last screen with a required
 * answer silently missing — the create button would then refuse without saying
 * why, because the payload cannot be built. Walking back to the first
 * unanswered step asks the question instead.
 */
const firstUnansweredStep = (draft: OnboardingDraft): StepKey => {
  if (draft.step === 'done') return draft.step

  const detailsAnswered =
    Boolean(draft.name.trim()) &&
    slugifyHandle(draft.handle).length >= HANDLE_MIN_LENGTH &&
    Boolean(draft.companySize)
  if (!detailsAnswered) return 'details'

  if (!draft.industry || !draft.priority) return 'template'

  return draft.step
}

/**
 * The wizard's answers, kept across a reload.
 *
 * Stored in this browser rather than on the platform, because the platform has
 * nowhere to put them: every onboarding endpoint is addressed by the workspace
 * this form has not created yet. See `draftRepo` for what that costs.
 */
export const useOnboardingState = () => {
  const [draft, setDraft] = useState<OnboardingDraft>(() =>
    normalizeDraft(readDraft())
  )
  /**
   * Hover and keyboard focus are tracked separately. Sharing one value meant a
   * `mouseleave` cleared the spotlight even though an input still held focus —
   * the ring vanished instead of returning to the focused field.
   */
  const [focused, setFocused] = useState<SpotlightKey | null>(null)
  const [hovered, setHovered] = useState<SpotlightKey | null>(null)
  const [error, setError] = useState<StepError>(null)

  /** Pending hover clear, cancelled when the pointer reaches the next target. */
  const hoverClearRef = useRef<number>(undefined)

  useEffect(() => () => window.clearTimeout(hoverClearRef.current), [])

  useEffect(() => {
    // The flow is over on `done`, and a finished draft resumed later would put
    // the user back on a summary for a workspace they already have.
    if (draft.step === 'done') {
      clearDraft()

      return
    }
    writeDraft(draft)
  }, [draft])

  /** Spread onto a focusable control to point the spotlight at `key`. */
  const focusProps = useCallback(
    (key: SpotlightKey) => ({
      onFocus: () => setFocused(key),
      onBlur: () => setFocused((current) => (current === key ? null : current))
    }),
    []
  )

  /**
   * Spread onto a hoverable region.
   *
   * `mouseleave` does not clear immediately: sibling targets are separated by a
   * gap, so leaving one and entering the next are two events with the pointer
   * over neither in between. Clearing straight away made the ring blink off and
   * back on for every tile-to-tile move — including tiles that share a key.
   * The pending clear is cancelled by the next `mouseenter`.
   */
  const hoverProps = useCallback(
    (key: SpotlightKey) => ({
      onMouseEnter: () => {
        window.clearTimeout(hoverClearRef.current)
        setHovered(key)
      },
      onMouseLeave: () => {
        window.clearTimeout(hoverClearRef.current)
        hoverClearRef.current = window.setTimeout(
          () => setHovered(null),
          HOVER_CLEAR_DELAY_MS
        )
      }
    }),
    []
  )

  const patch = useCallback((next: Partial<OnboardingDraft>) => {
    setError(null)
    setDraft((current) => ({ ...current, ...next }))
  }, [])

  const setName = useCallback((name: string) => {
    setError(null)
    setDraft((current) => ({
      ...current,
      name,
      handle: current.handleEdited ? current.handle : slugifyHandle(name)
    }))
  }, [])

  const setHandle = useCallback((value: string) => {
    setError(null)
    setDraft((current) => ({
      ...current,
      handle: sanitizeHandleInput(value),
      handleEdited: true
    }))
  }, [])

  /**
   * Canonicalises what typing was allowed to leave behind — a trailing dash, a
   * slug that does not start with a letter. Called when the field is left, so
   * the correction never lands under the cursor mid-word.
   */
  const normalizeHandle = useCallback(() => {
    setDraft((current) => {
      const next = slugifyHandle(current.handle)

      return next === current.handle ? current : { ...current, handle: next }
    })
  }, [])

  const addInvite = useCallback(() => {
    setDraft((current) => ({
      ...current,
      invites: [...current.invites, newInvite()]
    }))
  }, [])

  const removeInvite = useCallback((id: string) => {
    setDraft((current) => ({
      ...current,
      invites:
        current.invites.length > 1
          ? current.invites.filter((invite) => invite.id !== id)
          : current.invites
    }))
  }, [])

  const updateInvite = useCallback(
    (id: string, next: Partial<Omit<DraftInvite, 'id'>>) => {
      setError(null)
      setDraft((current) => ({
        ...current,
        invites: current.invites.map((invite) =>
          invite.id === id ? { ...invite, ...next } : invite
        )
      }))
    },
    []
  )

  /**
   * The name once typing has paused. Only the workspace tile reads it — the
   * tile's colour is hashed from the name, and recolouring per keystroke turns
   * typing a word into a run through the palette. Everything that shows the
   * name as *text* keeps using `draft.name`, which stays live.
   */
  const settledName = useDebouncedValue(draft.name.trim(), AVATAR_SETTLE_MS)

  /** Emails the user actually typed, deduplicated case-insensitively. */
  const filledInvites = useMemo(() => {
    const seen = new Set<string>()

    return draft.invites.reduce<DraftInvite[]>((acc, invite) => {
      const email = invite.email.trim()
      const key = email.toLowerCase()
      if (!email || seen.has(key)) return acc
      seen.add(key)
      acc.push({ ...invite, email })

      return acc
    }, [])
  }, [draft.invites])

  /**
   * Validates the current step, reporting the offending field alongside the
   * message. The attribution step has no rule — it is optional by design.
   */
  const validateStep = useCallback((): StepError => {
    switch (draft.step) {
      case 'details': {
        if (!draft.name.trim()) {
          return {
            field: 'name',
            key: 'workspaces.onboarding.errors.nameRequired'
          }
        }
        // Measured on the canonical slug, not on what is in the field: typing
        // is allowed to leave a trailing dash, and `ab-` is three characters
        // that become a two-character address.
        if (slugifyHandle(draft.handle).length < HANDLE_MIN_LENGTH) {
          return {
            field: 'handle',
            key: 'workspaces.onboarding.errors.handleTooShort'
          }
        }
        if (!draft.companySize) {
          return {
            field: 'companySize',
            key: 'workspaces.onboarding.errors.sizeRequired'
          }
        }

        return null
      }
      case 'template': {
        if (!draft.industry) {
          return {
            field: 'industry',
            key: 'workspaces.onboarding.errors.industryRequired'
          }
        }
        if (!draft.priority) {
          return {
            field: 'priority',
            key: 'workspaces.onboarding.errors.priorityRequired'
          }
        }

        return null
      }
      case 'invite': {
        const invalid = filledInvites.some(
          (invite) => !isInviteEmail(invite.email)
        )

        return invalid
          ? {
              field: 'invites',
              key: 'workspaces.onboarding.errors.inviteInvalid'
            }
          : null
      }
      default:
        return null
    }
  }, [draft, filledInvites])

  const goToStep = useCallback((step: StepKey) => {
    setError(null)
    setFocused(null)
    setHovered(null)
    setDraft((current) => ({ ...current, step }))
  }, [])

  /** Advances one step when the current one validates. Returns whether it moved. */
  const goNext = useCallback((): boolean => {
    const failure = validateStep()
    if (failure) {
      setError(failure)

      return false
    }
    setFocused(null)
    setHovered(null)
    setDraft((current) => {
      const next = STEP_ORDER[stepIndexOf(current.step) + 1]

      // Leaving the step commits the canonical slug — Enter never blurs the
      // field, so the on-blur pass would be skipped for keyboard users.
      return next
        ? { ...current, step: next, handle: slugifyHandle(current.handle) }
        : current
    })

    return true
  }, [validateStep])

  /** Steps back one, no validation — answers are kept, so a step is re-entered. */
  const goBack = useCallback(() => {
    setError(null)
    setFocused(null)
    setHovered(null)
    setDraft((current) => {
      const prev = STEP_ORDER[stepIndexOf(current.step) - 1]

      return prev ? { ...current, step: prev } : current
    })
  }, [])

  /**
   * Remember "I'll make my own anyway" in the persisted draft. Kept here rather
   * than in page state so a reload does not forget it and resurrect the fork when
   * the name field is emptied.
   */
  const declineJoin = useCallback(() => {
    setDraft((current) =>
      current.joinDeclined ? current : { ...current, joinDeclined: true }
    )
  }, [])

  return {
    draft,
    // Hover wins while the pointer is over a target; otherwise the focused
    // field keeps the ring.
    focus: hovered ?? focused,
    focusProps,
    hoverProps,
    error,
    settledName,
    stepIndex: stepIndexOf(draft.step),
    filledInvites,
    // Exposed so the caller can learn *which* field failed before advancing.
    // `goNext` sets `error` through setState, so reading `error` in the same
    // tick still returns the previous value — it would report the wrong field.
    validateStep,
    patch,
    setName,
    setHandle,
    normalizeHandle,
    addInvite,
    removeInvite,
    updateInvite,
    goToStep,
    goNext,
    goBack,
    declineJoin
  }
}

export type OnboardingState = ReturnType<typeof useOnboardingState>
